/**
 * The plugin's review dock, componentized.
 *
 * Same information in the same order, because this is the surface the product
 * is judged on: the verdict with its reasons, closure and area in the
 * document's own unit, every course with the words it was read from, and the
 * refusals named rather than hidden. `review_dock.py` is the reference; where
 * this differs it is because a browser affords something Qt did not, never
 * because something was left out.
 */

import type {
  Adjustment,
  BearingModel,
  Correction,
  HeldResponse,
  ServerVerdict,
  SuggestedLocation,
  Tract,
} from "../types";
import { VERDICT_COLOR } from "../types";
import {
  aliquotChain,
  bearingText,
  closureText,
  computedAreaText,
  easementLines,
  elide,
  referenceText,
  statedAreaText,
  unitLabel,
  unitOf,
} from "../format";
import { groupsByAnchor, isPlaceable, singleRing, type Ring } from "../place";
import { CallTable, type PendingChange } from "./CallTable";
import { closable, closeLabel } from "../corrections";

export interface ReviewProps {
  held: HeldResponse;
  onPlace: (rings: Ring[]) => void;
  onPlaceGeoref: (tract: Tract, verdict: ServerVerdict) => void;
  onPlaceSuggested: (tract: Tract, verdict: ServerVerdict) => void;
  placedTracts: Set<string>;
  /**
   * Post a correction, or a request to close a tract. Absent when the payload
   * cannot be corrected — a batch result carries no attestation of its own to
   * re-sign, so its tables stay read-only rather than offering an edit that
   * would fail at the service.
   */
  onCorrect?: (change: CorrectionRequest) => void;
  correcting?: boolean;
}

/** One tract's worth of editing, as the app posts it. */
export interface CorrectionRequest {
  tract: number;
  call?: number;
  kind?: "boundary" | "tie";
  changes?: PendingChange[];
  note?: string;
  /** Set instead of `changes` to fit the tract by compass rule. */
  close?: boolean;
}

export function Review({
  held,
  onPlace,
  onPlaceGeoref,
  onPlaceSuggested,
  placedTracts,
  onCorrect,
  correcting = false,
}: ReviewProps) {
  const {
    extraction,
    groundtruth_verdicts: verdicts,
    document_verdict: documentVerdict,
  } = held.parsed;
  const tracts = extraction.tracts ?? [];
  const groups = groupsByAnchor(tracts, verdicts);

  return (
    <div class="review">
      <Metadata held={held} />
      {held.parsed.input_kind === "text" && (
        <p class="muted small">
          Read from provided text, not a recorded document. The verdicts mean
          the same thing; the certificate names the source.
        </p>
      )}
      {extraction.notes && <p class="muted">{extraction.notes}</p>}
      {documentVerdict && (
        <div
          class="banner"
          style={{ borderLeftColor: VERDICT_COLOR[documentVerdict.status] }}
        >
          <strong>
            {documentVerdict.document}: {documentVerdict.status}
          </strong>
          {documentVerdict.summary && <div>{documentVerdict.summary}</div>}
          <ul>
            {documentVerdict.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      )}
      {(extraction.plat || extraction.as_built) && (
        <SheetCard sheet={(extraction.plat ?? extraction.as_built)!} />
      )}

      {!tracts.length && !documentVerdict && (
        <p class="refusal">
          No locatable description found in this document. Deeds and easements
          describe land by courses or by PLSS chains; lot-and-block conveyances
          and easement areas that live only on an attached drawing are recorded
          in the metadata above rather than drawn.
        </p>
      )}

      {[...groups.entries()].map(([anchor, rings]) => (
        <button
          key={anchor}
          type="button"
          class="primary wide"
          title={anchor}
          onClick={() => onPlace(rings)}
        >
          Place all {rings.length} tracts as a group · click: {elide(anchor)}
        </button>
      ))}

      {tracts.map((tract, index) => (
        <TractCard
          key={`${tract.name}-${index}`}
          tract={tract}
          verdict={verdicts[index]}
          placed={placedTracts.has(tract.name)}
          onPlace={() => onPlace([singleRing(tract, verdicts[index])])}
          onPlaceGeoref={() => onPlaceGeoref(tract, verdicts[index])}
          onPlaceSuggested={() => onPlaceSuggested(tract, verdicts[index])}
          onCorrect={
            onCorrect &&
            ((change: Omit<CorrectionRequest, "tract">) =>
              onCorrect({ ...change, tract: index }))
          }
          correcting={correcting}
          log={(held.parsed.corrections ?? []).filter((c) => c.tract === index)}
          adjustment={(held.parsed.adjustments ?? []).find(
            (a) => a.tract === index,
          )}
        />
      ))}
    </div>
  );
}

function Metadata({ held }: { held: HeldResponse }) {
  const metadata = held.parsed.extraction.metadata;
  if (!metadata) return null;
  const parties = [metadata.grantors?.join(", "), metadata.grantees?.join(", ")]
    .filter(Boolean)
    .join(" → ");
  const whenWhere = [
    referenceText(metadata.recording),
    metadata.transfer_date ?? "",
    [metadata.county, metadata.state].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div class="muted meta">
      {parties && <div>{parties}</div>}
      {whenWhere && <div>{whenWhere}</div>}
      {(metadata.prior_references ?? []).map((ref, i) => (
        <div key={i}>
          cites {referenceText(ref)}
          {ref.note ? ` — ${ref.note}` : ""}
        </div>
      ))}
      {(metadata.lot_block ?? []).map((lot, i) => (
        <div key={i}>
          plat ref:{" "}
          {[
            lot.lot ? `Lot ${lot.lot}` : "",
            lot.block ? `Block ${lot.block}` : "",
            lot.subdivision ?? "",
            lot.plat_book || lot.plat_page
              ? `(Plat Book ${lot.plat_book ?? ""}, Page ${lot.plat_page ?? ""})`
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        </div>
      ))}
    </div>
  );
}

function SheetCard({ sheet }: { sheet: Record<string, unknown> }) {
  const rows = Object.entries(sheet).filter(
    ([key, value]) =>
      value && key !== "verbatim_text" && typeof value !== "object",
  );
  const lists = Object.entries(sheet).filter(
    ([, value]) => Array.isArray(value) && value.length,
  );
  if (!rows.length && !lists.length) return null;
  return (
    <div class="card">
      {rows.map(([key, value]) => (
        <div key={key}>
          <span class="muted">{key.replace(/_/g, " ")}</span> {String(value)}
        </div>
      ))}
      {lists.map(([key, value]) => (
        <div key={key}>
          <span class="muted">{key.replace(/_/g, " ")}</span>{" "}
          {(value as unknown[]).join(", ")}
        </div>
      ))}
    </div>
  );
}

interface TractCardProps {
  tract: Tract;
  verdict: ServerVerdict;
  placed: boolean;
  onPlace: () => void;
  onPlaceGeoref: () => void;
  onPlaceSuggested: () => void;
  onCorrect?: (change: Omit<CorrectionRequest, "tract">) => void;
  correcting: boolean;
  log: Correction[];
  adjustment?: Adjustment;
}

function TractCard({
  tract,
  verdict,
  placed,
  onPlace,
  onPlaceGeoref,
  onPlaceSuggested,
  onCorrect,
  correcting,
  log,
  adjustment,
}: TractCardProps) {
  const georef = verdict.georef?.rings?.length ? verdict.georef : null;
  const unit = unitOf(tract);
  return (
    <section class="tract">
      <h3>
        <span
          class="badge"
          style={{ background: VERDICT_COLOR[verdict.status] }}
        >
          {verdict.status}
        </span>
        <span class="tract-name">{tract.name}</span>
        <span class="muted">{statedAreaText(tract)}</span>
        {tract.is_exception && <span class="muted"> · SAVE AND EXCEPT</span>}
      </h3>

      {verdict.geometry && (
        <p class="measures">
          {closureText(verdict)} · {computedAreaText(tract, verdict)}
        </p>
      )}

      {verdict.reasons.map((reason) => (
        <p
          key={reason}
          class="reason"
          style={{ color: VERDICT_COLOR[verdict.status] }}
        >
          • {reason}
        </p>
      ))}

      {tract.easement && (
        <div class="card">
          {easementLines(tract.easement).map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}

      {tract.pob_description && (
        <p class="muted small">Point of beginning: {tract.pob_description}</p>
      )}

      {!!tract.calls?.length && (
        <>
          {tract.description_type === "centerline" && (
            <p class="muted small">
              Centerline courses — the strip follows this line
            </p>
          )}
          <CallTable
            calls={tract.calls}
            unit={unit}
            edit={
              onCorrect && {
                kind: "boundary",
                busy: correcting,
                onSave: (call, changes, note) =>
                  onCorrect({ call, kind: "boundary", changes, note }),
              }
            }
          />
        </>
      )}

      {!!tract.tie_calls?.length && (
        <>
          <p class="muted small">
            Tie courses ({tract.tie_calls.length}) — not part of the boundary
          </p>
          <CallTable
            calls={tract.tie_calls}
            unit={unit}
            edit={
              onCorrect && {
                kind: "tie",
                busy: correcting,
                onSave: (call, changes, note) =>
                  onCorrect({ call, kind: "tie", changes, note }),
              }
            }
          />
        </>
      )}

      <OperatorLog log={log} adjustment={adjustment} unit={unit} />

      {onCorrect && closable(verdict) && !adjustment && (
        <p class="place-row">
          <button
            type="button"
            class="link"
            disabled={correcting}
            onClick={() => onCorrect({ close: true })}
            title="Fits the courses by compass rule so the shape closes. The verdict does not change: the recorded description still miscloses, and the certificate says so."
          >
            {closeLabel(verdict, unitLabel(unit))}
          </button>
        </p>
      )}

      {tract.aliquot && (
        <p class="muted small">
          PLSS: {aliquotChain(tract.aliquot.parts)} of Section{" "}
          {tract.aliquot.section}, T{tract.aliquot.township_number}
          {tract.aliquot.township_direction} R{tract.aliquot.range_number}
          {tract.aliquot.range_direction}
        </p>
      )}

      {verdict.location && <LocationCard location={verdict.location} />}

      {georef ? (
        <button
          type="button"
          class="primary"
          onClick={onPlaceGeoref}
          disabled={placed}
        >
          {placed ? "Placed" : `Place ${tract.name} at its PLSS location`}
        </button>
      ) : isPlaceable(verdict) ? (
        verdict.location?.pob ? (
          // One primary action, not two. The card's heading already names the
          // tract, and stacking a second full-width button under the first
          // reads as a choice the operator has to make before doing anything.
          <p class="place-row">
            <button
              type="button"
              class="primary"
              onClick={onPlaceSuggested}
              disabled={placed}
              title="Drops the point of beginning at the suggested position. Nothing is placed until you confirm, and you can move it first."
            >
              {placed ? "Placed" : "Place at the suggestion"}
            </button>
            {!placed && (
              <button type="button" class="link" onClick={onPlace}>
                or click the map
              </button>
            )}
          </p>
        ) : (
          <button
            type="button"
            class="primary"
            onClick={onPlace}
            disabled={placed}
          >
            {placed ? "Placed" : `Place ${tract.name} on the map`}
          </button>
        )
      ) : (
        <p class="muted small">
          Not traversable, so there is nothing to place:{" "}
          {verdict.reasons.join("; ") || "no geometry was returned for it"}
        </p>
      )}
    </section>
  );
}

/**
 * What a person changed on this tract, and what was fitted.
 *
 * Shown beside the courses rather than only on the certificate, because an
 * operator working through a second document should be able to see what the
 * first pass already changed. The two entries say different things on purpose:
 * a correction moved the verdict, an adjustment did not.
 */
function OperatorLog({
  log,
  adjustment,
  unit,
}: {
  log: Correction[];
  adjustment?: Adjustment;
  unit: string;
}) {
  if (!log.length && !adjustment) return null;
  const mark = unitLabel(unit);
  return (
    <div class="card">
      {!!log.length && (
        <div>
          <strong>Operator corrections</strong>
        </div>
      )}
      {log.map((entry, index) => (
        <div key={index} class="muted small">
          • {entry.kind === "tie" ? "tie " : ""}course {entry.call + 1}{" "}
          {String(entry.field).replace(/_/g, " ")}: {logValue(entry.before)} →{" "}
          {logValue(entry.after)}
          {entry.by ? ` · ${entry.by}` : ""}
          {entry.note ? ` — ${entry.note}` : ""}
        </div>
      ))}
      {adjustment && (
        <>
          <div>
            <strong>Geometry adjusted</strong> ·{" "}
            {adjustment.method.replace(/_/g, " ")},{" "}
            {adjustment.misclosure_before.toFixed(2)} {mark} distributed
          </div>
          <div class="muted small">
            The recorded courses do not close. The verdict above judges the
            record and was not recomputed over this fit.
          </div>
        </>
      )}
    </div>
  );
}

function logValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return bearingText(value as BearingModel);
  if (typeof value === "number") return value.toFixed(2);
  return String(value);
}

/**
 * The suggestion, and what was compared to arrive at it.
 *
 * Two shapes on purpose. With a position, the card names the monument and the
 * assumptions, so the operator can disagree before placing anything. Without
 * one, the card is the whole answer: which corner was tried and why nothing is
 * offered. The second shape is the one that earns the feature, because a wrong
 * position dropped quietly is worse than a blank map.
 */
function LocationCard({ location }: { location: SuggestedLocation }) {
  return (
    <div class="card">
      <div>
        <strong>Suggested location</strong>
        {location.basis ? ` · ${location.basis}` : ""}
      </div>
      {!location.pob && <div>No position suggested.</div>}
      {(location.reasons ?? []).map((reason) => (
        <div key={reason} class="muted small">
          • {reason}
        </div>
      ))}
      {location.plss_id && (
        <div class="muted small">PLSS {location.plss_id}</div>
      )}
    </div>
  );
}
